import { useEffect, useState } from "preact/hooks";
import { apiFetch, apiJson } from "../api";
import type { Tenant } from "../api";

interface Props {
  tenants: Tenant[];
  reload: () => Promise<void>;
}

export function Tenants({ tenants, reload }: Props) {
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <section>
      <header class="row">
        <h2>Tenants</h2>
        <button onClick={() => setCreating(true)}>+ Tenant</button>
      </header>

      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Short domain</th>
            <th>Base URL</th>
            <th>Default lang</th>
            <th>Rewrite</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.id}>
              <td>
                <code>{t.code}</code>
              </td>
              <td>{t.short_domain}</td>
              <td class="truncate">{t.base_redirect_url}</td>
              <td>{t.default_language}</td>
              <td>
                {t.url_rewrite_enabled
                  ? `/${t.url_rewrite_from ?? "?"}/ → /{country}/`
                  : "off"}
              </td>
              <td>
                <button onClick={() => setEditing(t)}>Edit</button>
                <button
                  onClick={async () => {
                    if (!confirm(`Delete tenant ${t.code}? This removes all URLs and KV keys.`))
                      return;
                    await apiFetch(`/api/tenants/${t.code}`, { method: "DELETE" });
                    await reload();
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {creating && (
        <TenantForm
          tenant={null}
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <TenantForm
          tenant={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}
    </section>
  );
}

interface FormProps {
  tenant: Tenant | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function TenantForm({ tenant, onClose, onSaved }: FormProps) {
  const [code, setCode] = useState(tenant?.code ?? "");
  const [domain, setDomain] = useState(tenant?.short_domain ?? "");
  const [baseUrl, setBaseUrl] = useState(tenant?.base_redirect_url ?? "");
  const [lang, setLang] = useState(tenant?.default_language ?? "EN");
  const [rewriteFrom, setRewriteFrom] = useState(tenant?.url_rewrite_from ?? "en");
  const [rewriteOn, setRewriteOn] = useState(Boolean(tenant?.url_rewrite_enabled));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        code,
        short_domain: domain,
        base_redirect_url: baseUrl,
        default_language: lang,
        url_rewrite_from: rewriteFrom || null,
        url_rewrite_enabled: rewriteOn,
      };
      if (tenant) {
        await apiJson(`/api/tenants/${tenant.code}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await apiJson(`/api/tenants`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      await onSaved();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="modal">
      <form onSubmit={submit} class="card">
        <h3>{tenant ? `Edit ${tenant.code}` : "Create tenant"}</h3>
        <label>
          Code
          <input
            value={code}
            disabled={!!tenant}
            onInput={(e) => setCode((e.target as HTMLInputElement).value.toUpperCase())}
            required
          />
        </label>
        <label>
          Short domain
          <input
            value={domain}
            onInput={(e) => setDomain((e.target as HTMLInputElement).value)}
            required
          />
        </label>
        <label>
          Base redirect URL
          <input
            value={baseUrl}
            onInput={(e) => setBaseUrl((e.target as HTMLInputElement).value)}
            required
          />
        </label>
        <label>
          Default language / country
          <input
            value={lang}
            onInput={(e) => setLang((e.target as HTMLInputElement).value.toUpperCase())}
            required
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={rewriteOn}
            onInput={(e) => setRewriteOn((e.target as HTMLInputElement).checked)}
          />
          Rewrite `/from/` to `/{"{"}country{"}"}/`
        </label>
        <label>
          Rewrite-from segment
          <input
            value={rewriteFrom}
            disabled={!rewriteOn}
            onInput={(e) => setRewriteFrom((e.target as HTMLInputElement).value)}
          />
        </label>
        {err && <div class="error">{err}</div>}
        <div class="row">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={busy}>
            {busy ? "..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
